// app/lib/device-setup.client.ts
// Client-side utilities for device setup and metadata collection

/**
 * Get or create a unique device serial based on browser fingerprint
 * Falls back to localStorage for persistence across sessions
 */
export function getOrCreateDeviceSerial(): string {
  if (typeof window === "undefined") {
    return "unknown-device";
  }

  // Check if we have a stored serial
  const stored = localStorage.getItem("radiomisr_device_serial");
  if (stored) {
    return stored;
  }

  // Generate a new serial based on browser fingerprint
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    screen.width,
    screen.height,
    new Date().getTimezoneOffset(),
  ].join("|");

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  const serial = `device-${Math.abs(hash).toString(36)}-${Date.now().toString(36)}`;

  // Store for future use
  localStorage.setItem("radiomisr_device_serial", serial);

  return serial;
}

/**
 * Get comprehensive device metadata for registration
 */
export function getDeviceMetadata(): {
  userAgent: string;
  language: string;
  platform: string;
  screenWidth: number;
  screenHeight: number;
  timezoneOffset: number;
  hardwareConcurrency?: number;
  deviceMemory?: number;
  connectionType?: string;
  timestamp: string;
} {
  if (typeof window === "undefined") {
    return {
      userAgent: "unknown",
      language: "unknown",
      platform: "unknown",
      screenWidth: 0,
      screenHeight: 0,
      timezoneOffset: 0,
      timestamp: new Date().toISOString(),
    };
  }

  const metadata: any = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screenWidth: screen.width,
    screenHeight: screen.height,
    timezoneOffset: new Date().getTimezoneOffset(),
    timestamp: new Date().toISOString(),
  };

  // Add optional properties if available
  if ("hardwareConcurrency" in navigator) {
    metadata.hardwareConcurrency = (navigator as any).hardwareConcurrency;
  }

  if ("deviceMemory" in navigator) {
    metadata.deviceMemory = (navigator as any).deviceMemory;
  }

  if ("connection" in navigator) {
    const conn = (navigator as any).connection;
    if (conn) {
      metadata.connectionType = conn.effectiveType || conn.type || "unknown";
    }
  }

  return metadata;
}
